import React, { useState, useEffect, useMemo } from 'react'
import { ErrorPage, LoadingPage } from '@wallyhealth/web-core'
import moment from 'moment'
import { useFetchExperiment, generateBookingSlotsForLocation } from './hooks'
import BookPage from './BookPage'
import { prepSubscription } from './api'
import InfoFormPage from 'pages/InfoFormPage'
import PaymentPage from 'components/PaymentPage'
import useQuery from 'hooks/useQuery'
import heapService from 'services/heapService'
import { useGeos } from 'hooks/useGeos'

const Steps = {
  BOOK: 'BOOK',
  FORM: 'FORM',
  PAY: 'PAY'
}

const BookAndJoinFlow = () => {
  const query = useQuery()
  const experimentId = query.get('eid')

  const [step, setStep] = useState(Steps.BOOK)
  const [locationId, setLocationId] = useState(null)
  const [selectedSlot, setSelectedSlot] = useState({})
  const [info, setInfo] = useState(null)

  const { locations } = useGeos()
  const { experiment, isLoadingExperiment, isExperimentActive } = useFetchExperiment({ externalId: experimentId })

  const bookingSlots = useMemo(() => {
    if (!experiment?.config || !locationId) return []
    return generateBookingSlotsForLocation(experiment.config, locationId)
  }, [experiment?.config, locationId])

  const scrollToTop = () => (
    window.scrollTo({ top: 0, behavior: 'smooth' })
  )
  useEffect(() => {
    scrollToTop()
  }, [step])

  useEffect(() => {
    setLocationId(experiment?.config?.locationIds?.[0] || null)
  }, [experiment])

  const storeHeapUserProperties = () => {
    heapService.storeUserProperties({
      phone: info.phone,
      email: info.email,
      costInCents: experiment?.config?.paymentAmountInCents,
      location: resolveLocation().name,
      planType: experiment?.config?.billingPlanType,
      couponCode: null
    })
  }

  const availableLocations = useMemo(() => {
    const locationIds = experiment?.config?.locationIds ?? []
    return locations.filter(it => it.isActive)
      .filter(it => locationIds.includes(it.id))
  }, [experiment?.config?.locationIds])

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot)
    setStep(Steps.FORM)
  }

  const getFriendlyTime = (time) => {
    return moment(selectedSlot.slot.startDateTimeLocal).local().format('dddd, M/D/YY h:mm A')
  }

  const resolveLocation = () => {
    return locations.find(it => it.id === locationId)
  }

  const handleInfo = (info) => {
    setInfo(info)
    setStep(Steps.PAY)
  }
  const handleSubscriptionPrep = () => {
    storeHeapUserProperties()

    return prepSubscription({
      patient: info,
      planPublicId: experiment?.config?.billingPlanPublicId,
      location: resolveLocation(),
      welcomeMessageId: experiment?.config?.welcomeMessageId,
      tasks: [
        {
          taskType: 'GROWTH',
          text: `Handle ${experiment.name} booking`,
          details: `${getFriendlyTime()} at ${resolveLocation().name}`
        }
      ]
    })
  }

  if (isLoadingExperiment) {
    return (
      <LoadingPage />
    )
  }

  if (!experimentId || !isExperimentActive) {
    return (
      <ErrorPage
        title="Hm..."
        description="Looks like this link has expired."
      />
    )
  }

  return (
    <div>
      {step === Steps.FORM &&
        <InfoFormPage
          onSubmit={handleInfo}
          viewName="BookAndJoinInfoFormPage"
        />
      }
      {step === Steps.BOOK &&
        <BookPage
          slots={bookingSlots}
          selectedSlot={selectedSlot}
          onSlotSelected={handleSlotSelect}
          locationId={locationId}
          onLocationIdSelected={setLocationId}
          locations={availableLocations}
          title={experiment?.config?.schedulePageTitle}
        />
      }
      {step === Steps.PAY &&
        <PaymentPage
          customer={info}
          totalCostInCents={experiment?.config?.paymentAmountInCents}
          planType={experiment?.config?.billingPlanType}
          prepSubscription={handleSubscriptionPrep}
          viewName="BookAndJoinPaymentPage"
          title={experiment?.config?.paymentPageTitle}
          description={experiment?.config?.paymentPageDescription}
        />
      }
    </div>
  )
}

export default BookAndJoinFlow
